import React, { useState, useEffect, useRef } from 'react';
import AccountForm from '../Accounts/AccountForm';
import { pushModalToStack, removeModalFromStack, isTopModal } from './modalStack';
import UnsavedChangesPopover from './UnsavedChangesPopover';

const AccountFormModal = ({
  account,
  onSave,
  onCancel,
  debtors = [],
  organizers = [],
  etps = [],
  onCreateOwner,
  onEditOwner,
  initialPurpose,
  initialOwnerType,
  initialOwnerId,
  lockPurpose = false,
  lockOwnerType = false,
  lockOwnerId = false,
  forcedOwnerIdsByType = {},
  onOwnerSelected,
}) => {
  const initialData = {
    account_number: account?.account_number || '',
    bank_name: account?.bank_name || '',
    bik: account?.bik || '',
    corr_account: account?.corr_account || '',
    owner_type: account?.owner_type || initialOwnerType || 'platform',
    owner_id: account?.owner_id || initialOwnerId || '',
    purpose: account?.purpose || initialPurpose || 'deposit'
  };

  const [formData, setFormData] = useState(initialData);
  const [initialFormData, setInitialFormData] = useState(initialData);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const modalIdRef = useRef(`account_modal_${Date.now()}_${Math.random().toString(16).slice(2)}`);

  // Обновление формы при изменении account prop
  useEffect(() => {
    const newData = {
      account_number: account?.account_number || '',
      bank_name: account?.bank_name || '',
      bik: account?.bik || '',
      corr_account: account?.corr_account || '',
      owner_type: account?.owner_type || initialOwnerType || 'platform',
      owner_id: account?.owner_id || initialOwnerId || '',
      purpose: account?.purpose || initialPurpose || 'deposit'
    };
    setFormData(newData);
    setInitialFormData(JSON.parse(JSON.stringify(newData)));
  }, [account, initialOwnerId, initialOwnerType, initialPurpose]);

  // Проверка наличия изменений
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);

  useEffect(() => {
    const id = modalIdRef.current;
    pushModalToStack(id);
    return () => removeModalFromStack(id);
  }, []);

  // Обработка Escape с приоритетом
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (!isTopModal(modalIdRef.current)) return;
        if (showConfirmDialog) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleClose();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [hasChanges, showConfirmDialog]);

  // Обработка Escape для диалога подтверждения
  useEffect(() => {
    if (showConfirmDialog) {
      const handleConfirmEscape = (e) => {
        if (e.key === 'Escape') {
          if (!isTopModal(modalIdRef.current)) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowConfirmDialog(false);
          return false;
        }
      };

      document.addEventListener('keydown', handleConfirmEscape, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleConfirmEscape, { capture: true });
      };
    }
  }, [showConfirmDialog]);

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onCancel();
  };

  const handleSave = async (data, { exit } = {}) => {
    try {
      const result = await onSave(data, { exit });

      if (result === false) {
        return false;
      }

      const payload = typeof result === 'object' && result !== null ? result : data;
      const snapshot = JSON.parse(JSON.stringify(payload));
      setFormData(snapshot);
      setInitialFormData(snapshot);

      if (exit) {
        setShowConfirmDialog(false);
        onCancel();
      }

      return result;
    } catch (error) {
      console.error('Error saving account:', error);
      return false;
    }
  };

  return (
    <>
      <div className="modal-overlay entity-form-modal-overlay" style={{ zIndex: 3200 }}>
        <div className="modal modal-lg">
          <div className="modal-header">
            <h3>{account ? 'Редактирование банковского счета' : 'Создание банковского счета'}</h3>
            <button className="close-btn" onClick={handleClose}>&times;</button>
          </div>
          <div className="modal-body">
            <AccountForm
              account={account}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSave}
              onCancel={handleClose}
              isViewMode={false}
              debtors={debtors}
              organizers={organizers}
              etps={etps}
              onCreateOwner={onCreateOwner}
              onEditOwner={onEditOwner}
              lockPurpose={lockPurpose}
              lockOwnerType={lockOwnerType}
              lockOwnerId={lockOwnerId}
              forcedOwnerIdsByType={forcedOwnerIdsByType}
              onOwnerSelected={onOwnerSelected}
            />
          </div>
        </div>
      </div>

      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={13300}
      />
    </>
  );
};

export default AccountFormModal;
